import React, { useState, useEffect } from 'react';
import { menuItemsAPI, menuCategoriesAPI } from '../../services/api';
import ImageUpload from './ImageUpload';

function MenuItems() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    availability: 'all',
    search: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    image_url: '',
    is_available: true,
    is_featured: false,
    allergens: [],
    preparation_time: '',
    display_order: 0
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchItems();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [itemsResponse, categoriesResponse] = await Promise.all([
        menuItemsAPI.getAll(),
        menuCategoriesAPI.getAll()
      ]);
      setItems(itemsResponse.data.data);
      setCategories(categoriesResponse.data.data);
    } catch (err) {
      setError('Failed to load data');
      console.error('Menu items error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      const params = {};
      if (filters.category !== 'all') params.category_id = filters.category;
      if (filters.availability !== 'all') params.is_available = filters.availability === 'available';
      
      const response = await menuItemsAPI.getAll(params);
      let filteredItems = response.data.data;

      // Apply search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredItems = filteredItems.filter(item =>
          item.name.toLowerCase().includes(searchTerm) ||
          item.description.toLowerCase().includes(searchTerm)
        );
      }

      setItems(filteredItems);
    } catch (err) {
      console.error('Failed to fetch items:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        category_id: parseInt(formData.category_id),
        preparation_time: formData.preparation_time ? parseInt(formData.preparation_time) : null,
        allergens: formData.allergens.filter(allergen => allergen.trim() !== '')
      };

      if (editingItem) {
        await menuItemsAPI.update(editingItem.id, submitData);
      } else {
        await menuItemsAPI.create(submitData);
      }
      
      await fetchItems();
      handleCloseModal();
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to save menu item';
      alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      price: item.price.toString(),
      category_id: item.category_id.toString(),
      image_url: item.image_url || '',
      is_available: item.is_available,
      is_featured: item.is_featured,
      allergens: item.allergens || [],
      preparation_time: item.preparation_time?.toString() || '',
      display_order: item.display_order
    });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await menuItemsAPI.delete(item.id);
      await fetchItems();
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to delete menu item';
      alert(message);
    }
  };

  const toggleAvailability = async (item) => {
    try {
      await menuItemsAPI.update(item.id, {
        ...item,
        is_available: !item.is_available
      });
      await fetchItems();
    } catch (err) {
      alert('Failed to update item availability');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category_id: '',
      image_url: '',
      is_available: true,
      is_featured: false,
      allergens: [],
      preparation_time: '',
      display_order: 0
    });
  };

  const handleImageUploaded = (imageUrl) => {
    setFormData(prev => ({ ...prev, image_url: imageUrl }));
  };

  const handleImageRemoved = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAllergenChange = (index, value) => {
    const newAllergens = [...formData.allergens];
    newAllergens[index] = value;
    setFormData(prev => ({ ...prev, allergens: newAllergens }));
  };

  const addAllergen = () => {
    setFormData(prev => ({ ...prev, allergens: [...prev.allergens, ''] }));
  };

  const removeAllergen = (index) => {
    const newAllergens = formData.allergens.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, allergens: newAllergens }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p className="mt-3">Loading menu items...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
        <button className="btn btn-outline-danger ms-3" onClick={fetchData}>
          <i className="fas fa-redo me-2"></i>Retry
        </button>
      </div>
    );
  }

  return (
    <div className="menu-items">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="page-title">Menu Items</h2>
          <p className="page-subtitle">
            Manage your restaurant's food and beverage offerings
          </p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowModal(true)}
        >
          <i className="fas fa-plus me-2"></i>
          Add Menu Item
        </button>
      </div>

      {/* Filters */}
      <div className="filters-card mb-4">
        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label">Search Items</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or description..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Category</label>
            <select
              className="form-select"
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Availability</label>
            <select
              className="form-select"
              value={filters.availability}
              onChange={(e) => setFilters(prev => ({ ...prev, availability: e.target.value }))}
            >
              <option value="all">All Items</option>
              <option value="available">Available Only</option>
              <option value="unavailable">Unavailable Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Items List */}
      {items.length > 0 ? (
        <div className="items-grid">
          {items.map((item) => (
            <div key={item.id} className="item-card">
              {/* Item Image */}
              {item.image_url && (
                <div className="item-image">
                  <img 
                    src={`http://localhost:3001${item.image_url}`} 
                    alt={item.name}
                    className="menu-item-image"
                  />
                </div>
              )}

              <div className="item-header">
                <div className="item-title-section">
                  <h5 className="item-name">{item.name}</h5>
                  <span className="item-category">{item.category_name}</span>
                </div>
                <div className="item-price">
                  JD{item.price}
                </div>
              </div>

              <div className="item-body">
                {item.description && (
                  <p className="item-description">{item.description}</p>
                )}

                <div className="item-details">
                  {item.allergens && item.allergens.length > 0 && (
                    <div className="detail-item">
                      <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                      <small>Allergens: {item.allergens.join(', ')}</small>
                    </div>
                  )}
                  
                  {item.preparation_time && (
                    <div className="detail-item">
                      <i className="fas fa-clock text-info me-2"></i>
                      <small>Prep time: {item.preparation_time} minutes</small>
                    </div>
                  )}
                </div>

                <div className="item-badges">
                  <span className={`badge ${item.is_available ? 'bg-success' : 'bg-secondary'}`}>
                    {item.is_available ? 'Available' : 'Unavailable'}
                  </span>
                  {item.is_featured && (
                    <span className="badge bg-warning text-dark">Featured</span>
                  )}
                </div>
              </div>

              <div className="item-actions">
                <button
                  className={`btn btn-sm ${item.is_available ? 'btn-outline-warning' : 'btn-outline-success'}`}
                  onClick={() => toggleAvailability(item)}
                  title={item.is_available ? 'Mark as Unavailable' : 'Mark as Available'}
                >
                  <i className={`fas ${item.is_available ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleEdit(item)}
                  title="Edit Item"
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDelete(item)}
                  title="Delete Item"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state text-center py-5">
          <i className="fas fa-utensils fa-4x text-muted mb-3"></i>
          <h4 className="text-muted">No Menu Items Found</h4>
          <p className="text-muted mb-4">
            {filters.search || filters.category !== 'all' || filters.availability !== 'all'
              ? 'Try adjusting your filters to see more items'
              : 'Start by adding your first menu item to showcase your offerings'
            }
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            <i className="fas fa-plus me-2"></i>
            Add Menu Item
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCloseModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Item Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Grilled Chicken Sandwich"
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Category *</label>
                      <select
                        className="form-select"
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select a category</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      rows="3"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe the dish, ingredients, and any special preparation..."
                    ></textarea>
                  </div>

                  {/* Image Upload Component */}
                  <ImageUpload
                    currentImage={formData.image_url ? `http://localhost:3001${formData.image_url}` : null}
                    onImageUploaded={handleImageUploaded}
                    onImageRemoved={handleImageRemoved}
                  />

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Price (JD) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        required
                        placeholder="0.00"
                      />
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Preparation Time (minutes)</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        name="preparation_time"
                        value={formData.preparation_time}
                        onChange={handleInputChange}
                        placeholder="15"
                      />
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        name="display_order"
                        value={formData.display_order}
                        onChange={handleInputChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Allergens</label>
                    {formData.allergens.map((allergen, index) => (
                      <div key={index} className="input-group mb-2">
                        <input
                          type="text"
                          className="form-control"
                          value={allergen}
                          onChange={(e) => handleAllergenChange(index, e.target.value)}
                          placeholder="e.g., gluten, dairy, nuts"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeAllergen(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={addAllergen}
                    >
                      <i className="fas fa-plus me-2"></i>Add Allergen
                    </button>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="is_available"
                          id="is_available"
                          checked={formData.is_available}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="is_available">
                          Available for ordering
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="is_featured"
                          id="is_featured"
                          checked={formData.is_featured}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="is_featured">
                          Featured item
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        {editingItem ? 'Update' : 'Create'} Item
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showModal && <div className="modal-backdrop fade show"></div>}

      <style jsx>{`
        .page-title {
          color: #2c3e50;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #6c757d;
          margin-bottom: 0;
        }

        .filters-card {
          background: white;
          border-radius: 15px;
          padding: 1.5rem;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }

        .items-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .item-card {
          background: white;
          border-radius: 15px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .item-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }

        .item-image {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .menu-item-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .item-card:hover .menu-item-image {
          transform: scale(1.05);
        }

        .item-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .item-name {
          margin: 0;
          font-weight: 600;
          font-size: 1.1rem;
        }

        .item-category {
          font-size: 0.8rem;
          opacity: 0.8;
          display: block;
          margin-top: 0.25rem;
        }

        .item-price {
          font-size: 1.25rem;
          font-weight: 700;
          background: rgba(255, 255, 255, 0.2);
          padding: 0.5rem 1rem;
          border-radius: 25px;
        }

        .item-body {
          padding: 1.5rem;
        }

        .item-description {
          color: #6c757d;
          margin-bottom: 1rem;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .item-details {
          margin-bottom: 1rem;
        }

        .detail-item {
          display: flex;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .item-badges {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .item-actions {
          display: flex;
          gap: 0.5rem;
          padding: 1rem 1.5rem;
          background-color: #f8f9fa;
          border-top: 1px solid #e9ecef;
        }

        .empty-state {
          background: white;
          border-radius: 15px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          margin: 2rem 0;
        }

        .modal {
          z-index: 1050;
        }

        .modal-backdrop {
          z-index: 1040;
        }

        .modal-content {
          border: none;
          border-radius: 15px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .modal-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          border-radius: 15px 15px 0 0;
        }

        .modal-title {
          color: #2c3e50;
          font-weight: 600;
        }

        .form-control:focus,
        .form-select:focus {
          border-color: #c9302c;
          box-shadow: 0 0 0 0.2rem rgba(201, 48, 44, 0.25);
        }

        .form-check-input:checked {
          background-color: #c9302c;
          border-color: #c9302c;
        }

        .input-group .btn {
          border-color: #ced4da;
        }
      `}</style>
    </div>
  );
}

export default MenuItems;